import { apiClient } from './apiClient';
import {
  Document,
  DocumentCategory,
  DocumentPermission,
  DocumentComment,
} from '@/hooks/shared/useDocuments';

interface DocumentVersion {
  id: string;
  tenantId: string;
  documentId: string;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  filePath: string;
  contentType: string;
  checksum: string;
  versionNotes?: string;
  uploadedBy: string;
  uploadedByName: string;
  isCurrentVersion: boolean;
  createdAt: string;
}

class DocumentService {
  // Documents
  async getDocuments(
    tenantId?: string,
    filters?: {
      categoryId?: string;
      category?: string;
      department?: string;
      access?: string;
      authorId?: string;
      tags?: string[];
    },
  ): Promise<Document[]> {
    // Convert categoryId to category for backwards compatibility
    const apiFilters = { ...filters };
    if (filters?.categoryId) {
      apiFilters.category = filters.categoryId;
      delete apiFilters.categoryId;
    }

    return apiClient.get<Document[]>('/documents', {
      tenantId,
      ...apiFilters,
    });
  }

  async getDocument(documentId: string): Promise<Document> {
    return apiClient.get<Document>(`/documents/${documentId}`);
  }

  async createDocument(
    documentData: Omit<
      Document,
      | 'id'
      | 'status'
      | 'downloadCount'
      | 'viewCount'
      | 'shareCount'
      | 'hasComments'
      | 'hasVersions'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<Document> {
    return apiClient.post<Document>('/documents', documentData);
  }

  async updateDocument(documentId: string, updates: Partial<Document>): Promise<Document> {
    return apiClient.put<Document>(`/documents/${documentId}`, updates);
  }

  async deleteDocument(documentId: string): Promise<void> {
    await apiClient.delete(`/documents/${documentId}`);
  }

  async uploadDocument(
    tenantId: string,
    file: File,
    documentData: Partial<Document>,
  ): Promise<Document> {
    return apiClient.upload<Document>('/documents/upload', file, { tenantId, ...documentData });
  }

  async downloadDocument(documentId: string, format?: string): Promise<string> {
    // Return download URL instead of Blob for hook compatibility
    const response = await fetch(
      `${this.baseUrl}/documents/${documentId}/download${format ? `?format=${format}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    // Create object URL from blob for download
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async shareDocument(
    documentId: string,
    shareData: {
      emails?: string[];
      groups?: string[];
      message?: string;
      expirationDate?: string;
    },
  ): Promise<{ shareLink?: string; shareId: string }> {
    // Transform hook format to service format
    const serviceShareData = {
      shareType: shareData.emails?.length ? ('email' as const) : ('internal' as const),
      recipients: shareData.emails || shareData.groups || [],
      expiresAt: shareData.expirationDate,
      message: shareData.message,
      allowDownload: true,
      allowComments: true,
    };

    return apiClient.post(`/documents/${documentId}/share`, serviceShareData);
  }

  // Categories
  async getCategories(tenantId?: string): Promise<DocumentCategory[]> {
    return this.getDocumentCategories(tenantId);
  }

  async createCategory(
    tenantId: string,
    categoryData: Omit<
      DocumentCategory,
      | 'id'
      | 'tenantId'
      | 'displayOrder'
      | 'versionControlEnabled'
      | 'approvalRequired'
      | 'documentCount'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<DocumentCategory> {
    const fullCategoryData = {
      tenantId,
      displayOrder: 0,
      versionControlEnabled: false,
      approvalRequired: false,
      documentCount: 0,
      ...categoryData,
    };
    return this.createDocumentCategory(fullCategoryData);
  }

  async getDocumentCategories(tenantId?: string): Promise<DocumentCategory[]> {
    return apiClient.get<DocumentCategory[]>('/documents/categories', { tenantId });
  }

  async createDocumentCategory(
    categoryData: Omit<DocumentCategory, 'id' | 'documentCount' | 'createdAt' | 'updatedAt'>,
  ): Promise<DocumentCategory> {
    return apiClient.post<DocumentCategory>('/documents/categories', categoryData);
  }

  async updateDocumentCategory(
    categoryId: string,
    updates: Partial<DocumentCategory>,
  ): Promise<DocumentCategory> {
    return apiClient.put<DocumentCategory>(`/documents/categories/${categoryId}`, updates);
  }

  async deleteDocumentCategory(categoryId: string): Promise<void> {
    await apiClient.delete(`/documents/categories/${categoryId}`);
  }

  // Permissions
  async getDocumentPermissions(documentId: string): Promise<DocumentPermission[]> {
    return apiClient.get<DocumentPermission[]>(`/documents/${documentId}/permissions`);
  }

  async updateDocumentPermissions(
    documentId: string,
    permissions: Omit<DocumentPermission, 'id' | 'documentId' | 'createdAt' | 'updatedAt'>[],
  ): Promise<DocumentPermission[]> {
    return apiClient.put<DocumentPermission[]>(`/documents/${documentId}/permissions`, {
      permissions,
    });
  }

  // Permission management methods expected by hooks
  async grantPermission(
    documentId: string,
    permissionData: Omit<
      DocumentPermission,
      'id' | 'tenantId' | 'documentId' | 'grantedAt' | 'createdAt'
    >,
  ): Promise<DocumentPermission> {
    return apiClient.post<DocumentPermission>(
      `/documents/${documentId}/permissions/grant`,
      permissionData,
    );
  }

  async revokePermission(permissionId: string): Promise<void> {
    await apiClient.delete(`/documents/permissions/${permissionId}`);
  }

  // Comments
  async getDocumentComments(documentId: string): Promise<DocumentComment[]> {
    return apiClient.get<DocumentComment[]>(`/documents/${documentId}/comments`);
  }

  async addDocumentComment(
    documentId: string,
    commentData: Omit<
      DocumentComment,
      'id' | 'documentId' | 'likeCount' | 'replyCount' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<DocumentComment> {
    return apiClient.post<DocumentComment>(`/documents/${documentId}/comments`, commentData);
  }

  async updateDocumentComment(
    commentId: string,
    updates: Partial<DocumentComment>,
  ): Promise<DocumentComment> {
    return apiClient.put<DocumentComment>(`/documents/comments/${commentId}`, updates);
  }

  async deleteDocumentComment(commentId: string): Promise<void> {
    await apiClient.delete(`/documents/comments/${commentId}`);
  }

  async likeDocumentComment(commentId: string, userId: string): Promise<DocumentComment> {
    return apiClient.post<DocumentComment>(`/documents/comments/${commentId}/like`, {
      userId,
    });
  }

  // Comment management method expected by hooks
  async addComment(
    documentId: string,
    commentData: Omit<
      DocumentComment,
      | 'id'
      | 'tenantId'
      | 'documentId'
      | 'status'
      | 'flagged'
      | 'replies'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<DocumentComment> {
    return apiClient.post<DocumentComment>(`/documents/${documentId}/comments`, commentData);
  }

  // Versions - Return Documents instead of DocumentVersions for hook compatibility
  async getDocumentVersions(documentId: string): Promise<Document[]> {
    const versions = await apiClient.get<DocumentVersion[]>(`/documents/${documentId}/versions`);

    // Transform DocumentVersion to Document format for hook compatibility
    const baseDocument = await this.getDocument(documentId);
    return versions.map((version) => ({
      ...baseDocument,
      id: version.id,
      versionNumber: version.versionNumber.toString(),
      filename: version.fileName,
      fileSizeBytes: version.fileSize,
      filePath: version.filePath,
      createdAt: version.createdAt,
      isCurrentVersion: version.isCurrentVersion,
    }));
  }

  async createDocumentVersion(
    documentId: string,
    file: File,
    versionNotes?: string,
  ): Promise<DocumentVersion> {
    return apiClient.upload<DocumentVersion>(`/documents/${documentId}/versions`, file, {
      versionNotes,
    });
  }

  async restoreDocumentVersion(versionId: string): Promise<Document> {
    return apiClient.post<Document>(`/documents/versions/${versionId}/restore`);
  }

  async downloadDocumentVersion(versionId: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/documents/versions/${versionId}/download`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Version download failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  // Search
  async searchDocuments(
    tenantId: string,
    query: string,
    filters?: {
      category?: string;
      department?: string;
      tags?: string[];
      dateRange?: { start: string; end: string };
    },
  ): Promise<Document[]> {
    return apiClient.get<Document[]>('/documents/search', {
      tenantId,
      query,
      ...filters,
    });
  }

  // Bulk Operations
  async bulkUpdateDocuments(
    documentIds: string[],
    updates: Partial<Document>,
  ): Promise<Document[]> {
    return apiClient.put<Document[]>('/documents/bulk-update', {
      documentIds,
      updates,
    });
  }

  async bulkDeleteDocuments(documentIds: string[]): Promise<void> {
    await apiClient.post('/documents/bulk-delete', {
      documentIds,
    });
  }

  // Analytics
  async getDocumentAnalytics(
    documentId?: string,
    timeRange?: string,
  ): Promise<{
    totalViews: number;
    totalDownloads: number;
    totalShares: number;
    topDocuments: Array<{ documentId: string; title: string; views: number }>;
    categoryUsage: Record<string, number>;
    userActivity: Array<{ userId: string; userName: string; activity: number }>;
  }> {
    return apiClient.get('/documents/analytics', {
      documentId,
      timeRange,
    });
  }

  private get baseUrl() {
    return import.meta.env.VITE_API_BASE_URL || '/api';
  }

  private getAuthToken(): string {
    return localStorage.getItem('authToken') || '';
  }
}

export const documentService = new DocumentService();
